import React from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FavoriteButton from '../../atoms/FavoriteButton';
import { useLanguageLink } from '../../hooks/useLanguageLink';
import { useTranslation } from '../../hooks/useTranslatedField';
import { DNGatsbyTemplate } from '../DriveNinjaTemplate/gatsby';
import ListItemIcon from '@mui/material/ListItemIcon';
import Grid from '@mui/material/Unstable_Grid2';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import CheckIcon from '@mui/icons-material/Check';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import { createIntl, FormattedMessage, FormattedNumber, IntlProvider } from 'react-intl';
import ar from './ar.json';
import en from './en.json';
import { LocaleProvider } from '../../../contexts/language';
import CarImageGallery from '../../molecules/CarImageGallery';
import { ComparisonContext } from '../../../contexts/comparison';
import ShareItemButton from '../../atoms/ShareItemButton';
import CarTitle from '../../atoms/CarTitle';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Paper from '@mui/material/Paper';
import OfferPriceBreakdown from '../../molecules/OfferPriceBreakdown';

const languages = {
  ar, en
};
import { GatsbyImage } from 'gatsby-plugin-image';
import { useCarLink } from '../../hooks/useCarLink';
import { GetHelpPanel } from '../../molecules/PureGetHelpPanel/GetHelpPanel';
import FinanceCalculator from '../../molecules/FinanceCalculator';
import { BrandContext } from '../../../contexts/branding';
import { useFormHandlers } from '../../molecules/FinanceCalculator/useFormHandlers';
import CarPriceBox from '../../atoms/CarPriceBox';

const ContactDealerForm = React.lazy(() => import( '../../molecules/Forms/ContactDealerForm'));

const StickyBottomRow = ({ children, sx, ...rest }) => (<Box {...rest} sx={{

  position: 'sticky',
  bottom: 0,
  zIndex: 799,
  backgroundColor: '#fff',
  ...sx
}}>{children}</Box>);
StickyBottomRow.defaultProps = {
  sx: {}
};
StickyBottomRow.propTypes={
  children:PropTypes.node,
  xs:PropTypes.shape({}),
};
const OfferFavoriteIcon = ({ uuid }) => {
  const { addOffer, removeOffer, compareConfig } = React.useContext(ComparisonContext);
  return <FavoriteButton
    outlined
    onToggleFavorite={() => compareConfig?.offers?.indexOf(uuid) === -1 ? addOffer(uuid) : removeOffer(uuid)}
    favorite={compareConfig?.offers?.indexOf(uuid) !== -1}
  />;
};
OfferFavoriteIcon.propTypes = {
  uuid: PropTypes.string.isRequired
};


export default function NewOfferPage({ pageContext: { lang, direction }, data }) {
  const intl = createIntl({ locale: lang || 'en', messages: languages[lang] || languages.en });
  const l = useLanguageLink();
  const t = useTranslation();
  const cl = useCarLink();
  const brandConfig = React.useContext(BrandContext);
  const [showContactDealer, setShowContactDealer] = React.useState(false);
  const { addEnquiry } = useFormHandlers();

  const [upfrontAmount, setUpfrontAmount] = React.useState(null);
  const [numberMonths, setNumberMonths] = React.useState(null);
  const [paymentType, setPaymentType] = React.useState('finance');

  const manufacturer = data.driveNinjaManufacturerType;
  const model = data.driveNinjaModelType;
  const trimLevel = data.driveNinjaTrimLevelType;
  const offer = data.driveNinjaPurchaseNewOfferType;
  const provider = offer?.provider;
  const trimLevelImages = trimLevel?.images?.filter(({
    interiorColor: ic, exteriorColor: ec
  }) => (ec?.slug && offer?.exteriorColor?.slug && ec?.slug === offer?.exteriorColor?.slug || ic?.slug && offer?.exteriorColor?.slug && ic?.slug === offer?.exteriorColor?.slug));
  const trimLevelFilteredImages = trimLevelImages.length ? trimLevelImages : null;
  const grayImages = trimLevel.grayImages?.length ? trimLevel.grayImages : null;
  const offerImages = (offer?.images?.length && offer?.images) || (trimLevelFilteredImages) || grayImages || null;

  const INTEREST_RATE = 2.5;
  const nm = numberMonths ? numberMonths : 60;
  const ua = (upfrontAmount ? upfrontAmount : (0.2 * offer.purchaseAmount));
  const principal = offer?.purchaseAmount - ua;
  const interest = INTEREST_RATE / 100 / 12;

  const x = Math.pow(1 + interest, nm);
  const monthlyAmount = (principal * x * interest) / (x - 1);

  return (<LocaleProvider localeConfig={{ lang, direction }}>
    <DNGatsbyTemplate
      breadcrumbs={[{
        name: intl.formatMessage({
          id: 'browseMake.BrowseMakeBreadcrumbs', defaultMessage: 'Browse by Manufacturer'
        }), path: l('/make/')
      }, {
        name: t(manufacturer.translatedName), path: cl({ mfrSlug: manufacturer.slug })
      }, {
        name: t(model.translatedName), path: cl({ mfrSlug: manufacturer.slug, mdlSlug: model?.slug })
      }, {
        name: t(trimLevel.translatedName),
        path: cl({ trimSlug: trimLevel?.slug, mfrSlug: manufacturer.slug, mdlSlug: model?.slug })
      }, {
        name: intl.formatMessage({
          id: 'newOfferBreadcrumb.name', defaultMessage: 'Buy new from {provider}'
        }, { provider: t(provider?.translatedName) }), path: cl({
          mfrSlug: manufacturer.slug, mdlSlug: model?.slug, trimSlug: trimLevel?.slug, newOfferUUID: offer?.uuid
        })
      }]}
    >
      <IntlProvider locale={lang} messages={languages[lang] || languages.en}>
        <React.Suspense fallback={null}>
          <ContactDealerForm onSubmit={addEnquiry} open={showContactDealer}
            offerId={offer?.id}
            modelId={model?.id}
            trimLevelId={trimLevel?.id}
            providerId={offer?.provider?.id}
            onClose={() => setShowContactDealer(false)} />
        </React.Suspense>
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Grid container spacing={3}>
            <Grid xs={12} md={7} lg={8}>
              <CarImageGallery
                stockAvailability={offer.stockAvailability}
                images={offerImages}
                colorName={(offer?.images?.length || trimLevelFilteredImages) ? undefined : t(offer?.exteriorColor?.translatedName)}
                colorCode={(offer?.images?.length || trimLevelFilteredImages) ? undefined : offer?.exteriorColor?.genericColor?.htmlColorCode}
              />
            </Grid>
            <Grid xs={12} md={5} lg={4}>
              <Stack spacing={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <CarTitle>{t(manufacturer.translatedName)} {t(trimLevel.translatedSingularName)}</CarTitle>
                </Box>
                <CarPriceBox sx={{ textDecoration: 'none' }} title={'View new offers'}>
                  <Stack direction={'row'} sx={{ justifyContent: 'space-between' }}>
                    <Box>
                      <FormattedMessage id={'NewOfferPage.newFrom__amount'} defaultMessage={'Buy New'} />
                    </Box>
                    <Box>
                      <FormattedNumber
                        // eslint-disable-next-line react/style-prop-object
                        style={'currency'}
                        value={offer.purchaseAmount}
                        currency={offer.currency}
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                      />
                    </Box>
                  </Stack>
                </CarPriceBox>
                <CarPriceBox sx={{ textDecoration: 'none' }}>
                  <Stack direction={'row'} sx={{ fontWeight: 600, justifyContent: 'space-between' }}>
                    <Box>
                      <FormattedMessage id={'NewOfferPage.financeNewFrom__amount'} defaultMessage={'Finance From'} />
                    </Box>
                    <Box>
                      <FormattedNumber
                        // eslint-disable-next-line react/style-prop-object
                        style={'currency'}
                        value={offer.monthlyAmount}
                        currency={offer.currency}
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                      />
                    </Box>
                  </Stack>
                </CarPriceBox>
                <Box>
                  <Typography variant={'h6'}>
                    <FormattedMessage id={'DealImageHeader.youFoundYourCar'} defaultMessage={'You found your car!'} />
                  </Typography>
                </Box>
                <Box
                  sx={{
                    fontSize: '14px'
                  }}>

                  <FormattedMessage id={'DealImageHeader.financeIntro'}
                    defaultMessage={'Let\'s get your finance and any other extras sorted and we\'ll have you behind the wheel in no time.'} />

                </Box>
                <Box>
                  <List dense type="secondary">
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <FormattedMessage id={'DealImageHeader.brandNewCar'} defaultMessage={'Brand new car'} />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <FormattedMessage id={'DealImageHeader.directFromDealer'}
                        defaultMessage={'Direct from an official UAE Dealership'} />
                    </ListItem>


                    {offer?.warrantyPeriodYears ? (

                      <ListItem>
                        <ListItemIcon>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <FormattedMessage values={{ warrantyPeriodYears: offer?.warrantyPeriodYears }}
                          id={'DealImageHeader.warrantyperiod'}
                          defaultMessage={'Includes {warrantyPeriodYears} year warranty'} />
                      </ListItem>

                    ) : null}

                    {offer?.freeServicePeriodYears && offer?.freeServicePeriodYears > 0 ? (<ListItem>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <FormattedMessage
                        id={'DealImageHeader.freeServicingPeriod'}
                        defaultMessage={'Free Servicing for the first {freeServicePeriodYears, plural, =0 {today} one {# year}  other {# years}}.'}
                        values={{ freeServicePeriodYears: offer?.freeServicePeriodYears }}
                      />
                    </ListItem>

                    ) : null}
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <FormattedMessage id={'DealImageHeader.easyBooking'}
                        defaultMessage={'The easiest way to book your car'} />
                    </ListItem>
                  </List>
                </Box>
                {provider.logoFile ? (<Stack>
                  <Box sx={{ fontSize: '12px', textAlign: 'center' }}>
                    <FormattedMessage id={'DealImageHeader.carProvider'} defaultMessage={'Sold By'} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {(provider && provider.logoFile && provider.logoFile.childImageSharp && provider.logoFile.childImageSharp.gatsbyImageData) ? (
                      <GatsbyImage image={provider.logoFile.childImageSharp.gatsbyImageData} />) : null}
                  </Box>
                </Stack>) : null}

                <Stack direction={'row'} spacing={1}>
                  <OfferFavoriteIcon uuid={offer?.uuid} />
                  <ShareItemButton
                    model={t(model.translatedName)}
                    make={t(manufacturer.translatedName)}
                    url={typeof window !== 'undefined' ? window.location.href : ''}
                  />
                </Stack>
              </Stack>
            </Grid>
            <Grid xs={12}>
              <Stack spacing={3}>
                <Paper
                  sx={{ p: 2 }}>
                  <Stack spacing={3}>
                    <FormControl>
                      <FormLabel
                        id={'payTypeGroup'}>
                        <Typography style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          margin: 0

                        }}>
                          <FormattedMessage
                            id={'purchaseConfigurator.paymentType.title'}
                            defaultMessage={'How would you like to pay?'}
                          />
                        </Typography>
                      </FormLabel>
                      <RadioGroup
                        aria-labelledby="payTypeGroup"
                        value={paymentType}
                        onChange={(e) => {
                          setPaymentType(e.target.value);
                        }}
                      >


                        <FormControlLabel
                          sx={{ fontSize: '12px' }}
                          control={<Radio />}
                          label={intl.formatMessage({
                            id: 'purchaseConfigurator.paymentType.monthly',
                            defaultMessage: 'Pay Monthly'
                          })}
                          value={'finance'}
                        />
                        <FormControlLabel
                          control={<Radio />}
                          label={intl.formatMessage({
                            id: 'purchaseConfigurator.paymentType.payAll',
                            defaultMessage: 'Pay with cash'
                          })}
                          value={'cash'}

                        />
                        {(brandConfig.hubspot.binanceFormId?.en) ? (
                          <FormControlLabel
                            control={<Radio />}
                            label={intl.formatMessage({
                              id: 'purchaseConfigurator.paymentType.payCrypto',
                              defaultMessage: 'Pay with crypto'
                            })}
                            value={'crypto'}
                          />
                        ) : null}
                      </RadioGroup>

                    </FormControl>
                    {paymentType === 'finance' ? (
                      <FinanceCalculator
                        offer={offer}
                        {...offer}
                        upfrontAmount={upfrontAmount || offer?.purchaseAmount * 0.2}
                        setUpfrontAmount={setUpfrontAmount}
                        minUpfrontAmount={0.2 * offer?.purchaseAmount}
                        maxUpfrontAmount={0.8 * offer?.purchaseAmount}
                        minNumberMonths={12}
                        maxNumberMonths={60}
                        numberMonths={numberMonths || 60}
                        setNumberMonths={setNumberMonths}
                      />
                    ) : null}

                  </Stack>
                </Paper>

                <GetHelpPanel
                  offer={offer}
                  offerId={offer?.id}
                  modelId={model?.id}
                  trimLevelId={trimLevel?.id}
                  providerId={offer?.provider?.id}
                />

                <StickyBottomRow>
                  <OfferPriceBreakdown
                    onSelect={() => setShowContactDealer(true)}
                    currency={offer?.currency}
                    purchaseAmount={(paymentType === 'cash' || paymentType === 'crypto') ? offer.purchaseAmount : undefined}
                    monthlyAmount={paymentType === 'finance' ? monthlyAmount : undefined}
                  />
                </StickyBottomRow>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </IntlProvider>
    </DNGatsbyTemplate>
  </LocaleProvider>);
}


NewOfferPage.propTypes = {
  pageContext: PropTypes.shape({
    lang: PropTypes.string.isRequired, direction: PropTypes.string.isRequired
  }), data: PropTypes.shape({
    driveNinjaManufacturerType: PropTypes.shape({
      translatedName: PropTypes.shape({
        ar: PropTypes.string, en: PropTypes.string.isRequired
      }).isRequired, slug: PropTypes.string.isRequired
    }), driveNinjaPurchaseNewOfferType: PropTypes.shape({
      images: PropTypes.arrayOf(PropTypes.shape({})),
      purchaseAmount: PropTypes.string.isRequired,
      currency: PropTypes.string.isRequired,
      monthlyAmount: PropTypes.string.isRequired,
      stockAvailability: PropTypes.string.isRequired,
      stockAvailabilityRank: PropTypes.number.isRequired,
      warrantyPeriodYears: PropTypes.number,
      freeServicePeriodYears: PropTypes.number,
      provider: PropTypes.shape({
        slug: PropTypes.string.isRequired, translatedName: PropTypes.shape({
          ar: PropTypes.string, en: PropTypes.string.isRequired
        }).isRequired
      }).isRequired,
      uuid: PropTypes.string.isRequired
    }), driveNinjaTrimLevelType: PropTypes.shape({
      translatedSingularName: PropTypes.shape({
        ar: PropTypes.string, en: PropTypes.string.isRequired
      }).isRequired, translatedName: PropTypes.shape({
        ar: PropTypes.string, en: PropTypes.string.isRequired
      }).isRequired, translatedDescription: PropTypes.shape({
        ar: PropTypes.string, en: PropTypes.string.isRequired
      }).isRequired, slug: PropTypes.string.isRequired, images: PropTypes.arrayOf(PropTypes.shape({
        orderingPriority: PropTypes.number.isRequired,
        thumbnail: PropTypes.shape({}),
        regularImage: PropTypes.shape({}),
        fullImage: PropTypes.shape({})
      })).isRequired, orderImages: PropTypes.arrayOf(PropTypes.shape({}))
    }), driveNinjaModelType: PropTypes.shape({
      translatedName: PropTypes.shape({
        ar: PropTypes.string, en: PropTypes.string.isRequired
      }).isRequired, slug: PropTypes.string.isRequired
    })
  })
};


export const query = graphql`
    query ($uuid: JSON!, $trimSlug: String!, $mdlSlug: String!, $mfrSlug: String!) {
        driveNinjaManufacturerType(slug: {eq: $mfrSlug}) {
            translatedName {
                en
                ar
            }
            slug
            id
        }
        driveNinjaTrimLevelType(slug: {eq: $trimSlug}) {
            id:remoteId
            slug
            translatedSingularName {
                ar
                en
            }
            grayImages:images {
                orderingPriority
                thumbnail: localImage {
                    childImageSharp {
                        gatsbyImageData(height: 100, width:100, layout: FIXED, transformOptions:{
                            fit:COVER,
                            cropFocus:ENTROPY
                        })
                    }
                }
                regularImage: localImage {
                    childImageSharp {
                        gatsbyImageData(width: 600, layout: FIXED,transformOptions:{grayscale:true})
                    }
                }
                fullImage: localImage {
                    childImageSharp {
                        gatsbyImageData(layout: FULL_WIDTH,transformOptions:{grayscale:true})
                    }
                }
            }
            images {
                orderingPriority
                thumbnail: localImage {
                    childImageSharp {
                        gatsbyImageData(height: 100, width:100, layout: FIXED, transformOptions:{
                            fit:COVER,
                            cropFocus:ENTROPY
                        })
                    }
                }
                regularImage: localImage {
                    childImageSharp {
                        gatsbyImageData(width: 600, layout: FIXED)
                    }
                }
                fullImage: localImage {
                    childImageSharp {
                        gatsbyImageData(layout: FULL_WIDTH)
                    }
                }
            }

            translatedName {
                en
                ar
            }
        }
        driveNinjaModelType(slug: {eq: $mdlSlug}) {
            translatedName {
                en
                ar
            }
            slug
            id: remoteId
        }
        driveNinjaPurchaseNewOfferType(uuid: {eq: $uuid}) {
            id:remoteId
            exteriorColor {
                slug
                id
                translatedName {
                    en
                    ar
                }
                htmlColorCode
                genericColor {
                    id
                    htmlColorCode
                }
            }
            interiorColor {
                id
                slug
                translatedName {
                    en
                    ar
                }
                htmlColorCode
                genericColor {
                    id
                    htmlColorCode
                }
            }
            provider {
                id:remoteId
                translatedName {
                    ar
                    en
                }
                slug
                logoFile {
                    childImageSharp {
                        gatsbyImageData(height: 50, layout: FIXED)
                        id
                    }
                }
            }
            uuid
            purchaseAmount
            currency
            monthlyAmount
            stockAvailability
            stockAvailabilityRank
            images {
                orderingPriority
                thumbnail: localImage {
                    childImageSharp {
                        gatsbyImageData(height: 100, width:100, layout: FIXED, transformOptions:{
                            fit:COVER,
                            cropFocus:CENTER
                        })
                    }
                }
                regularImage: localImage {
                    childImageSharp {
                        gatsbyImageData(width: 600, layout: FIXED)
                    }
                }
                fullImage: localImage {
                    childImageSharp {
                        gatsbyImageData(layout: FULL_WIDTH)
                    }
                }
            }
        }
    }
`;
